import './applicantResidenceDurationPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import { TWO_YEARS_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/twoYears';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    currentResidenceMonthsAt,
    isSyncingResidence,
    syncingResidenceFailedMessage,
    setCurrentResidenceMonthsAt,
    syncResidence,
  } = useApplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      monthsAtResidenceValue: currentResidenceMonthsAt,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const monthsAtResidenceValue = event.target.value;

    setIsInitialSelection(
      isEmpty(currentResidenceMonthsAt) && isValidClickEvent(event),
    );

    setCurrentResidenceMonthsAt(monthsAtResidenceValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { monthsAtResidenceValue } = values;

    if (!monthsAtResidenceValue) {
      return;
    }

    // Always sync residence on form submit in case user did not provide a duration initially
    const synced = await syncResidence();
    if (synced) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-residence-duration-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantResidenceDuration.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantResidenceDuration.form.h1}
              >
                {t('ApplicantResidenceDurationPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantResidenceDuration.form.h2}
              >
                {t('ApplicantResidenceDurationPage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantResidenceDuration.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="monthsAtResidenceValue"
                  options={[
                    {
                      label: t('ApplicantResidenceDurationPage.yes'),
                      value: TWO_YEARS_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.applicantResidenceDuration.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantResidenceDurationPage.no'),
                      value: ONE_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.applicantResidenceDuration.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantResidenceDuration.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.applicantResidenceDuration.form.submitButton
                  }
                  disabled={isEmpty(currentResidenceMonthsAt)}
                  loading={isSyncingResidence}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantResidenceDurationPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantResidenceDuration.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantResidenceDurationPage';
